import { SessionApi, UserApi as LegacyUserApi } from 'account-service-client';
import { createLogger } from 'logging';
import { SubscriptionsApi } from 'subscription-service-client';
import { UserApi } from 'user-service-client';

import { User } from '@videoblocks/events-ts/lib/storyblocks/common/User';

export class CommonRecordFactory {
    private static logger = createLogger('CommonRecordFactory');

    public static async user(
        sessionToken: string | undefined,
        visitorCookieId: string | null,
        newLoginPageFeatureFlag: boolean,
    ): Promise<User> {
        if (newLoginPageFeatureFlag) {
            return this.newUserEvent(sessionToken, visitorCookieId);
        }
        return this.legacyUserEvent(sessionToken, visitorCookieId);
    }

    private static async newUserEvent(sessionToken: string | undefined, visitorCookieId: string | null): Promise<User> {
        const userEvent = new User();
        // make sure all fields are defaulted to null instead of undefined
        userEvent.visitorCookieId = visitorCookieId;
        userEvent.recurlySubscriptionId = null;
        userEvent.recurlySubscriptionUuid = null;
        userEvent.subscriptionId = null;
        userEvent.userId = null;
        userEvent.organizationId = null;
        userEvent.bot = null;

        try {
            if (sessionToken) {
                const user = await UserApi.getUserBySession(sessionToken);
                if (user) {
                    userEvent.userId = user.id ?? null;
                    userEvent.organizationId = user?.organizations?.[0]?.id ?? null;

                    const userSubscriptions = await SubscriptionsApi.getSubscriptionsByUserId(user.id);
                    const subscription = userSubscriptions[0];
                    if (subscription) {
                        if (subscription.isLegacy) {
                            const subscriptionId = parseInt(subscription.id, 10);
                            if (isNaN(subscriptionId)) {
                                this.logger.error(
                                    'Invalid subscription ID for legacy subscription type: %s',
                                    subscription.id,
                                );
                            } else {
                                userEvent.subscriptionId = subscriptionId ?? null;
                            }
                        } else {
                            userEvent.recurlySubscriptionId = subscription.id ?? null;
                            userEvent.recurlySubscriptionUuid = subscription.uuid ?? null;
                        }
                    }
                }
            }
        } catch (error) {
            CommonRecordFactory.logger.error(
                error,
                'Error fetching user session information for new user event data. sessionToken: %s, visitorCookieId: %s',
                sessionToken,
                visitorCookieId,
            );
        }
        return userEvent;
    }

    private static async legacyUserEvent(
        sessionToken: string | undefined,
        visitorCookieId: string | null,
    ): Promise<User> {
        const userEvent = new User();
        userEvent.visitorCookieId = visitorCookieId;
        userEvent.recurlySubscriptionId = null;
        userEvent.recurlySubscriptionUuid = null;
        userEvent.subscriptionId = null;
        userEvent.userId = null;
        userEvent.organizationId = null;
        userEvent.bot = null;

        try {
            if (sessionToken) {
                const session = await SessionApi.getBySessionToken(sessionToken);
                if (session && session.userId) {
                    const user = await LegacyUserApi.getByUserId(session.userId);
                    if (user) {
                        userEvent.userId = session.userId ?? null;
                        userEvent.organizationId = user?.organizations?.[0]?.id ?? null;
                        userEvent.subscriptionId = user?.userSubscriptions?.[0]?.id ?? null;
                        userEvent.recurlySubscriptionId = null;
                        userEvent.recurlySubscriptionUuid = null;
                    }
                }
            }
        } catch (e) {
            CommonRecordFactory.logger.error(
                e,
                'Error fetching user session information for legacy user event data. sessionToken: %s, visitorCookieId: %s',
                sessionToken,
                visitorCookieId,
            );
        }
        return userEvent;
    }
}
